import React, { useEffect, useState } from 'react';
import { GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { Autocomplete, TextField } from '@mui/material';

interface StatusEditCellProps extends GridRenderEditCellParams {
  api: GridRenderEditCellParams['api'];
}

const STATUS_OPTIONS = ['Active', 'Installed', 'Pending Install', 'Disconnected', 'Cancelled', 'In Progress'];

const StatusEditCell: React.FC<StatusEditCellProps> = (props) => {
  const { api, id, value } = props;

  // Validate and default the status value
  const getValidStatus = (inputValue: string | undefined | null): string => {
    if (!inputValue || typeof inputValue !== 'string') {
      return 'Disconnected'; // Default value
    }

    // If the value is already valid, return it
    if (STATUS_OPTIONS.includes(inputValue)) {
      return inputValue;
    }

    // Try to find a case-insensitive match
    const normalizedInput = inputValue.toLowerCase().trim();
    const matchedStatus = STATUS_OPTIONS.find((status) => status.toLowerCase() === normalizedInput);

    if (matchedStatus) {
      return matchedStatus;
    }

    // If no match found, default to 'Disconnected'
    return 'Disconnected';
  };

  const [selectedStatus, setSelectedStatus] = useState<string>(getValidStatus(value));

  // Update when the value prop changes
  useEffect(() => {
    const validStatus = getValidStatus(value);
    setSelectedStatus(validStatus);

    // If the value was invalid and we corrected it, update the grid immediately
    if (value !== validStatus) {
      api.setEditCellValue({ id, field: props.field, value: validStatus });
    }
  }, [value, api, id, props.field]);

  const handleChange = (_event: React.SyntheticEvent, newValue: string | null) => {
    const validStatus = getValidStatus(newValue);
    setSelectedStatus(validStatus);

    // Update the grid value
    api.setEditCellValue({ id, field: props.field, value: validStatus });
  };

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
      <Autocomplete
        value={selectedStatus}
        onChange={handleChange}
        options={STATUS_OPTIONS}
        getOptionLabel={(option) => option}
        disableClearable
        fullWidth
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiInputBase-root': {
                height: '100%',
                fontSize: '0.875rem',
                border: 'none'
              },
              '& .MuiInputBase-input': {
                padding: '8px',
                height: 'auto'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '& .MuiInputLabel-root': {
                display: 'none'
              }
            }}
          />
        )}
        ListboxProps={{
          style: {
            maxHeight: 200
          }
        }}
      />
    </div>
  );
};

export default StatusEditCell;
